import React from 'react';
import VoucherCard from '../gift-cards-v2/VoucherCard';
import LoyaltyCard from '../loyalty-v2/LoyaltyCard';
import { DESIGNS, getDesign } from '../gift-cards-v2/data';
import { buildPreviewVoucher } from '../giftcard-shop/shopUtils';
// VoucherCard styles are scoped under `.voucher-v2`, so we wrap the card in it.
import '../gift-cards-v2/GiftCardsV2.css';

export const fmtExpiry = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Resolve how a wallet item should render its card. A card "needs a design"
// (and therefore shows the Choose-a-design flow) whenever it would otherwise
// fall back to VoucherCard's empty custom placeholder — i.e. no design id, an
// id that no longer resolves to a known design, or a custom design saved
// without artwork. POS-sold / gifted cards stay here until the owner picks one.
// Checking the rendered outcome (not just `!item.designId`) keeps the salon-
// facing "Build one in the Designs tab" placeholder off the client side.
export const resolveWalletDesign = (item) => {
  const design = item && item.designId ? (getDesign(DESIGNS, item.designId) || null) : null;
  const voucher = buildPreviewVoucher(design, item ? item.balance : 0);
  const aesthetic = design ? design.aesthetic : 'custom';
  const undesigned = aesthetic === 'custom' && !voucher.customDesign;
  return { design, voucher, aesthetic, undesigned };
};

// WalletCard — one owned gift card: the real rendered design (showing the
// remaining balance as the face value) plus a balance/expiry row beneath. When
// no design has been chosen yet (bought in salon / gifted), it shows a "choose a
// design" placeholder instead of a rendered card.
// One owned loyalty card: the salon's live design rendered with this
// client's progress (the same LoyaltyCard the salon designs with — never a
// baked image, so a design change updates every wallet instantly).
const WalletLoyaltyCard = ({ item }) => {
  const { scheme, state } = item;
  const isStamps = scheme.mode === 'stamps';
  const rewards = state.rewardsAvailable || 0;
  const progressLine = isStamps
    ? `${state.filled || 0} of ${scheme.stampsRequired || 0} stamps`
    : `${state.balance || 0} points${scheme.pointsTarget ? ` of ${scheme.pointsTarget}` : ''}`;
  return (
    <div className="wallet-card" style={{ cursor: 'default' }}>
      <div className="wallet-card-art" style={{ padding: 0, background: 'transparent' }}>
        <LoyaltyCard design={item.design} scheme={scheme} state={state} salonName={item.salon.name} width={300} />
      </div>
      <div className="wallet-card-row">
        <span className="wallet-card-balance">
          {progressLine}
          {rewards > 0 && (
            <span className="wallet-card-balance-label">
              {` · ${rewards} reward${rewards === 1 ? '' : 's'} ready`}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

const WalletCard = ({ item, onClick }) => {
  if (item.kind === 'loyalty') return <WalletLoyaltyCard item={item} />;
  const { voucher, aesthetic, undesigned } = resolveWalletDesign(item);
  const partlySpent = item.faceValue !== item.balance;
  const expiry = fmtExpiry(item.expiresAt);

  return (
    <button type="button" className="wallet-card" onClick={() => onClick && onClick(item)}>
      {undesigned ? (
        <div className="wallet-card-art wallet-undesigned">
          <span className="wallet-undesigned-icon" aria-hidden>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.1 6.3a3 3 0 0 0 1.6 1.6L22 12l-6.3 2.1a3 3 0 0 0-1.6 1.6L12 22l-2.1-6.3a3 3 0 0 0-1.6-1.6L2 12l6.3-2.1a3 3 0 0 0 1.6-1.6L12 2z" /></svg>
          </span>
          <span className="wallet-undesigned-title">Design not chosen yet</span>
          <span className="wallet-undesigned-cta">Choose a design</span>
        </div>
      ) : (
        <div className="voucher-v2 wallet-card-art">
          <VoucherCard
            voucher={voucher}
            aesthetic={aesthetic}
            salonName={item.salon.name}
            currency={item.currency}
            showPromo={false}
          />
        </div>
      )}
      <div className="wallet-card-row">
        <span className="wallet-card-balance">
          {item.currency}{item.balance}
          <span className="wallet-card-balance-label">
            {partlySpent ? ` left of ${item.currency}${item.faceValue}` : ' balance'}
          </span>
        </span>
        {expiry && <span className="wallet-card-expiry">Expires {expiry}</span>}
      </div>
    </button>
  );
};

export default WalletCard;
